/**
 * Delivery Status Pipeline — single source of truth.
 *
 * 3 main statuses + 4 deliverable checkboxes that drive the progress bar.
 */

// ─── Main delivery statuses ─────────────────────────────────────────
export const DELIVERY_STATUSES = [
  "survey_scheduled",
  "in_progress",
  "delivered",
] as const;

export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

type StatusMeta = {
  label: string;
  /** Badge variant for rendering */
  badgeVariant: "default" | "secondary" | "outline";
  /** Optional extra className for badge */
  badgeClassName?: string;
};

export const DELIVERY_STATUS_CONFIG: Record<DeliveryStatus, StatusMeta> = {
  survey_scheduled: {
    label: "Survey Scheduled",
    badgeVariant: "secondary",
  },
  in_progress: {
    label: "In Progress",
    badgeVariant: "secondary",
  },
  delivered: {
    label: "Delivered",
    badgeVariant: "default",
    badgeClassName: "bg-status-success border-status-success-border",
  },
};

/**
 * Filterable delivery_status values for the status chips, in display order.
 * Distinct from DELIVERY_STATUSES (the 3 pipeline stages drawn by the progress
 * timeline): "no_survey_scheduled" (Pending) is a stored, filterable status but
 * NOT a pipeline stage, so it belongs here and not in DELIVERY_STATUSES.
 */
export const STATUS_FILTERS = [
  "no_survey_scheduled",
  "survey_scheduled",
  "in_progress",
  "delivered",
] as const;

export type StatusFilter = (typeof STATUS_FILTERS)[number];

/** Get the human-readable label for a status string */
export function getStatusLabel(status: string | null | undefined): string {
  if (status === "no_survey_scheduled") return "Pending";
  const s = (status ?? "in_progress") as DeliveryStatus;
  return DELIVERY_STATUS_CONFIG[s]?.label ?? status ?? "";
}

// ─── Deliverable checkboxes ─────────────────────────────────────────

export const DELIVERABLES = [
  "survey_completed",
  "report_completed",
  "vr_tours_completed",
  "files_documents_completed",
] as const;

export type DeliverableKey = (typeof DELIVERABLES)[number];

type DeliverableMeta = {
  label: string;
  /** Short label for the progress bar dot tooltip */
  shortLabel: string;
};

export const DELIVERABLE_CONFIG: Record<DeliverableKey, DeliverableMeta> = {
  survey_completed: {
    label: "Survey",
    shortLabel: "Survey",
  },
  report_completed: {
    label: "Report",
    shortLabel: "Report",
  },
  vr_tours_completed: {
    label: "VR Tours",
    shortLabel: "VR Tours",
  },
  files_documents_completed: {
    label: "Files & Documents",
    shortLabel: "Files & Docs",
  },
};

/** Count how many deliverables are completed */
export function countCompleted(
  property: Record<string, unknown> | null | undefined
): number {
  if (!property) return 0;
  return DELIVERABLES.filter((key) => property[key] === true).length;
}

/** Get overall percentage (0-100) based on completed deliverables */
export function getDeliverablePercent(
  property: Record<string, unknown> | null | undefined
): number {
  return Math.round((countCompleted(property) / DELIVERABLES.length) * 100);
}

// ─── Portfolio bar display config ───────────────────────────────────

/**
 * Stage order for the portfolio progress bar — most-complete first, so the
 * filled bar reads left-to-right as "done → not started".
 */
export const STAGE_ORDER: StatusFilter[] = [
  "delivered",
  "in_progress",
  "survey_scheduled",
  "no_survey_scheduled",
];

/** Tailwind background class per stage. Reuses emerald=delivered / amber=in-progress
 * already used by the per-row timeline. */
export const STAGE_BAR_COLOR: Record<StatusFilter, string> = {
  delivered: "bg-emerald-500",
  in_progress: "bg-amber-500",
  survey_scheduled: "bg-sky-500",
  no_survey_scheduled: "bg-muted-foreground/30",
};
